/**
 * Wordings of «Учитель» v3 — family «quiz» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * The quiz (docs/TEACHING-V3.md §2.4, truth §6.1): a question (`v3.quiz.q.*`, one sentence — the card shows it as the
 * question), three buttons (`v3.quiz.cat.*` — stages 1–2, the opponent in the 3rd person; `v3.quiz.opt.*` — goals in
 * the infinitive at stages 3–5, plus the fixed canCapture / checkEscape buttons), then `v3.quiz.right` or
 * `v3.quiz.wrong` and the true explanation (`v3.quiz.explain.*`). An explanation follows BOTH a right and a wrong
 * answer, so it never starts with «Да» / «Нет» and never judges the child's pick.
 */
import type { LessonWordings } from '../types.ts';

/** the younger band of a stage range (stages 1–2) and the older one (3–5) */
const Y = [1, 2] as const;
const O = [3, 5] as const;

export const WORDINGS: LessonWordings = {
  // ───────────────────────── questions ─────────────────────────

  /**
   * «Что задумал соперник?» Stages 1–2 answer with the fixed categories in the 3rd person («Нападает», «Съедает
   * фигуру», «Выводит фигуру»…) — the young wordings ask «что он делает»; stages 3–5 answer with goals in the infinitive
   * («Напасть на фигуру», «Занять центр») — the old wordings ask «чего он хочет / зачем / какая цель». The shared ones
   * («что задумал», «что на уме», «какой план») suit both kinds of buttons.
   */
  'v3.quiz.q.oppIdea': [
    { t: 'Как думаешь, что задумал соперник?' },
    { t: 'Что задумал соперник этим ходом?' },
    { t: 'Соперник сходил — что у него за план?' },
    { t: 'Интересно, что у соперника на уме?' },
    { t: 'Угадаешь, что затеял соперник?' },
    { t: 'Давай разгадаем соперника: что он задумал?' },
    { t: 'Что делает соперник, как считаешь?', stages: Y },
    { t: 'Посмотри на ход соперника: что он делает?', stages: Y },
    { t: 'Чем занят соперник?', stages: Y },
    { t: 'А ну-ка, что соперник делает?', stages: Y },
    { t: 'Соперник походил — что он теперь делает?', stages: Y },
    { t: 'Чего хочет соперник?', stages: O },
    { t: 'Зачем соперник так сходил?', stages: O },
    { t: 'Какая цель у хода соперника?', stages: O },
    { t: 'Ход соперника не случайный — зачем он так?', stages: O },
    { t: 'Разгадай план соперника: чего он добивается?', stages: O },
  ],

  /** «Какой фигурой лучше пойти?» — the buttons are in the instrumental («Конём», «Слоном», «Пешкой»). */
  'v3.quiz.q.whichPiece': [
    { t: 'Какой фигурой лучше пойти?' },
    { t: 'Как думаешь, какой фигурой сейчас сходить?' },
    { t: 'Кем сейчас пойдём, как считаешь?' },
    { t: 'Чем будем ходить, как думаешь?' },
    { t: 'Угадаешь, какой фигурой я бы пошёл?' },
    { t: 'Есть хороший ход — какой фигурой?' },
    { t: 'Сейчас важно выбрать фигуру: кем сходим?' },
    { t: 'Подумай, какой фигурой пойти.' },
    { t: 'Выбери, какой фигурой пойдём.' },
    { t: 'Кем из наших сходим?' },
    { t: 'Фигур много, а ход один — кем пойдём?' },
    { t: 'Кем сделаем ход?' },
  ],

  /** «Выгодно ли съесть {коня}?» — never «можно ли»: съесть можно всегда; the buttons: бесплатно / размен / потеряем. */
  'v3.quiz.q.canCapture': [
    { t: 'Выгодно ли съесть {коня}?' },
    { t: 'Как думаешь, стоит ли съесть {коня}?' },
    { t: 'Стоит ли сейчас брать {коня}?' },
    { t: 'Съесть {коня} — это выгодно?' },
    { t: 'Берём {коня} или лучше не стоит?' },
    { t: 'Подумай: выгодно ли нам забрать {коня}?' },
    { t: 'Как по-твоему, есть смысл съесть {коня}?' },
    { t: 'Посчитай: выгодно ли брать {коня}?' },
    { t: 'Интересно, стоит ли забирать {коня}?' },
    { t: '{Коня} можно взять — а стоит ли?' },
  ],

  /** «Шах! Как спасаемся?» in ONE sentence; the buttons: уйти королём / закрыться / съесть его. */
  'v3.quiz.q.checkEscape': [
    { t: 'Нам шах — как будем спасаться?' },
    { t: 'Королю шах: что будем делать?' },
    { t: 'Как спасём короля от шаха?' },
    { t: 'Нам объявили шах — как ответим?' },
    { t: 'Внимание, шах: как выручим короля?' },
    { t: 'Король под шахом — как поможем?' },
    { t: 'Как отбиться от этого шаха?' },
    { t: 'Что сделаем против шаха?' },
    { t: 'Ой, шах — как лучше спастись?' },
  ],

  /**
   * «Что соперник может съесть?» — the buttons are three kinds of our pieces; the threat is not highlighted yet.
   * Every wording asks for the OBJECT of «съесть / забрать / целиться в» (кого? что?), so the piece buttons read right
   * whether the engine labels them «Коня» or «Конь»; no «кто под ударом» / «кому грозит» (another case).
   */
  'v3.quiz.q.danger': [
    { t: 'Что соперник может съесть?' },
    { t: 'Кого из наших может съесть соперник?' },
    { t: 'Как думаешь, кого соперник хочет съесть?' },
    { t: 'Посмотри на наши фигуры: кого могут съесть?' },
    { t: 'Осторожно, соперник может кого-то съесть — кого?' },
    { t: 'Кого соперник может забрать прямо сейчас?' },
    { t: 'Соперник в кого-то целится — в кого?' },
    { t: 'Что может забрать у нас соперник?' },
    { t: 'Найди, кого из наших могут съесть.' },
    { t: 'Кто-то из наших в опасности — кого могут съесть?' },
  ],

  /** «Зачем мы так сходили?» (stages 3–5) — about the child's last move by the advice; the buttons are goals (infinitive). */
  'v3.quiz.q.why': [
    { t: 'Зачем мы так сходили?' },
    { t: 'Как думаешь, зачем был наш прошлый ход?' },
    { t: 'Для чего мы сделали прошлый ход?' },
    { t: 'Вспомни наш прошлый ход: что мы хотели?' },
    { t: 'Что мы задумали прошлым ходом?' },
    { t: 'Зачем я советовал этот ход?' },
    { t: 'Мы сходили по совету — а в чём была идея?' },
    { t: 'Проверим план: какая цель была у нашего хода?' },
    { t: 'Вот этот ход мы сделали по совету — для чего?' },
    { t: 'Какую задачу решал наш прошлый ход?' },
  ],

  // ───────────────────────── buttons: «Что задумал соперник?», stages 1–2 (3rd person) ─────────────────────────

  /**
   * `capture` is right when the opponent's move IS a capture (its «wrong» predicate is «не взятие», §6.1) — so the
   * label says what he does now («Съедает фигуру»), not «Хочет съесть»: a child reads «хочет съесть» as a threat, i.e.
   * the same as «Нападает», and would be told «wrong» for a true answer. `center` also fits a move that attacks the
   * centre without standing on it («Борется за центр»).
   */
  'v3.quiz.cat.attack': [{ t: 'Нападает' }, { t: 'Нападает на фигуру' }],
  'v3.quiz.cat.capture': [{ t: 'Съедает фигуру' }, { t: 'Забирает фигуру' }],
  'v3.quiz.cat.develop': [{ t: 'Выводит фигуру' }, { t: 'Будит фигуру' }],
  'v3.quiz.cat.center': [{ t: 'Занимает центр' }, { t: 'Борется за центр' }],
  'v3.quiz.cat.castle': [{ t: 'Прячет короля' }, { t: 'Делает рокировку' }],

  // ───────────────────────── buttons: goals, stages 3–5 (infinitive) ─────────────────────────

  'v3.quiz.opt.attack': [{ t: 'Напасть на фигуру' }, { t: 'Атаковать фигуру' }],
  'v3.quiz.opt.capture': [{ t: 'Съесть фигуру' }, { t: 'Забрать фигуру' }],
  'v3.quiz.opt.mate': [{ t: 'Поставить мат' }, { t: 'Грозить матом' }],
  'v3.quiz.opt.check': [{ t: 'Поставить шах' }, { t: 'Объявить шах' }],
  'v3.quiz.opt.fork': [{ t: 'Сделать вилку' }, { t: 'Напасть на двоих' }],
  'v3.quiz.opt.develop': [{ t: 'Вывести фигуру' }, { t: 'Разбудить фигуру' }],
  'v3.quiz.opt.center': [{ t: 'Занять центр' }, { t: 'Бороться за центр' }],
  'v3.quiz.opt.castle': [{ t: 'Спрятать короля' }, { t: 'Сделать рокировку' }],
  'v3.quiz.opt.defend': [{ t: 'Защитить фигуру' }, { t: 'Прикрыть фигуру' }],
  'v3.quiz.opt.escape': [{ t: 'Увести фигуру' }, { t: 'Уйти от удара' }],
  'v3.quiz.opt.trade': [{ t: 'Разменяться' }, { t: 'Пойти на размен' }],
  'v3.quiz.opt.queenOut': [{ t: 'Вывести ферзя' }, { t: 'Выпустить ферзя' }],
  'v3.quiz.opt.aimWeak': [{ t: 'Целиться в пешку' }, { t: 'Давить на пешку' }],
  /** openLine = a pawn move that frees a bishop or the queen — a diagonal OR a file, so not «открыть диагональ» */
  'v3.quiz.opt.openLine': [{ t: 'Открыть дорогу' }, { t: 'Освободить путь' }],
  'v3.quiz.opt.promote': [{ t: 'Пройти в ферзи' }, { t: 'Превратить пешку' }],
  'v3.quiz.opt.kingForward': [{ t: 'Повести короля' }, { t: 'Двинуть короля' }],

  // ───────────────────────── buttons: canCapture / checkEscape (every stage) ─────────────────────────

  'v3.quiz.opt.capYes': [{ t: 'Да, бесплатно' }, { t: 'Да, это подарок' }],
  'v3.quiz.opt.capTrade': [{ t: 'Будет размен' }, { t: 'Просто меняемся' }],
  'v3.quiz.opt.capLose': [{ t: 'Нет, потеряем' }, { t: 'Нет, будет хуже' }],
  'v3.quiz.opt.escKing': [{ t: 'Уйти королём' }, { t: 'Отойти королём' }],
  'v3.quiz.opt.escBlock': [{ t: 'Закрыться' }, { t: 'Загородиться' }],
  'v3.quiz.opt.escCapture': [{ t: 'Съесть его' }, { t: 'Съесть обидчика' }],

  // ───────────────────────── the reply to the answer ─────────────────────────

  /** A right answer: short and joyful, no «молодец»; the explanation follows. */
  'v3.quiz.right': [
    { t: 'Точно!' },
    { t: 'Верно!' },
    { t: 'В яблочко!' },
    { t: 'Именно так!' },
    { t: 'Да, так и есть!' },
    { t: 'Ага, я тоже так думаю!' },
    { t: 'Ты прав{g:|а}!' },
    { t: 'Глаз-алмаз!' },
    { t: 'Да, всё сходится!' },
    { t: 'Правильно!' },
    { t: 'Ух ты, в точку!' },
    { t: '{g:Сообразил|Сообразила}!' },
    { t: 'Ха, точно подмечено!' },
    { t: 'Ура, всё так!' },
    { t: 'Точь-в-точь!' },
  ],

  /** A wrong answer: gently, no «неправильно», «нет», «ошибка»; the same true explanation follows. */
  'v3.quiz.wrong': [
    { t: 'Хм, давай посмотрим вместе.' },
    { t: 'Интересная мысль, а на доске всё хитрее.' },
    { t: 'Мысль хорошая, но ответ другой.' },
    { t: 'Понимаю, почему так кажется, — давай разберёмся.' },
    { t: 'Тут есть секрет — сейчас покажу.' },
    { t: 'Ой, тут всё не так просто.' },
    { t: 'Так многие думают, а на деле по-другому.' },
    { t: 'Не совсем так — смотри, в чём дело.' },
    { t: 'А на самом деле тут вот что.' },
    { t: 'Вопрос был с подвохом — сейчас объясню.' },
    { t: 'Ничего страшного, сейчас всё станет ясно.' },
    { t: 'Шахматы полны сюрпризов — смотри.' },
  ],

  // ───────────────────────── the true answer explained ─────────────────────────

  /**
   * canCapture «бесплатно»: SEE ≥ 100 and nobody takes back — the target has no defender at all. The capture is only
   * «within 5 %» of the advice, not always the advice itself, so most wordings state the fact («можно съесть даром»)
   * instead of promising «берём»; the rest are «если съедим — …» in sense.
   */
  'v3.quiz.explain.capYes': [
    { t: '{Коня} можно съесть бесплатно — {он} без охраны.' },
    { t: 'Это настоящий подарок — {коня} никто не прикрывает.' },
    { t: 'Тут всё просто: {конь} {p:один|одна}, и помочь {ему} некому.' },
    { t: 'За {коня} никто не заступится — это как найти монетку.' },
    { t: 'У {коня:gen} нет защиты: съедим — и ничего не отдадим.' },
    { t: 'Съедим {коня} — и забрать обратно соперник не сможет.' },
    { t: 'Лёгкая добыча: {коня} можно брать смело.' },
  ],

  /** canCapture «размен»: SEE = 0, pieces of equal value — the target is defended, we lose as much as we take. */
  'v3.quiz.explain.capTrade': [
    { t: '{Коня} защищают: мы съедим — и у нас съедят, будет размен.' },
    { t: 'У {коня:gen} есть защита, поэтому выйдет честный размен.' },
    { t: 'Возьмём {коня} — соперник заберёт в ответ, получится поровну.' },
    { t: 'Получится обмен один на один — {конь} под защитой.' },
    { t: 'Это размен — {коня} есть кому защитить.' },
    { t: 'Если съедим {коня}, соперник съест в ответ — и сил останется столько же.' },
    { t: 'Здесь просто меняемся: {конь} под охраной.' },
  ],

  /**
   * canCapture «потеряем»: SEE ≤ −100 — the target is defended and our capturing piece is worth more than it
   * (SEE ≥ target − capturer), so «наша фигура дороже» / «фигуру подороже» is always true (a pawn never lands here).
   */
  'v3.quiz.explain.capLose': [
    { t: '{Коня} защищают, и мы потеряем больше, чем съедим.' },
    { t: '{Конь} под охраной: мы съедим {его}, а соперник — фигуру подороже.' },
    { t: 'За {коня} придётся заплатить слишком дорого.' },
    { t: 'Съесть {коня} невыгодно: в ответ у нас заберут больше.' },
    { t: 'Съесть {коня} — как отдать большую конфету за маленькую.' },
    { t: 'Тут {конь} под присмотром: съедим — и сами же пожалеем.' },
    { t: 'Лучше не трогать {коня}: {его} защищают, и мы только потеряем.' },
  ],

  /** checkEscape: the king steps away (no word on why the other ways are worse). */
  'v3.quiz.explain.escKing': [
    { t: 'Тут выручает сам король: он отходит в безопасное место.' },
    { t: 'Лучше уйти королём — одним шагом он спасётся от шаха.' },
    { t: 'Спасаемся королём — спрячем его туда, где шах не достанет.' },
    { t: 'Уходим королём — так надёжнее.' },
    { t: 'Королю лучше просто убежать от шаха.' },
    { t: 'Королю не нужны помощники — он уйдёт от шаха сам.' },
  ],

  /** checkEscape: block the check. */
  'v3.quiz.explain.escBlock': [
    { t: 'Здесь лучше закрыться: загородим короля своим бойцом.' },
    { t: 'Закрываемся — кто-то из наших заслонит короля.' },
    { t: 'Опустим перед шахом шлагбаум!' },
    { t: 'Надёжнее спрятать короля за спиной друга.' },
    { t: 'Встанем между королём и обидчиком — шах не пройдёт!' },
    { t: 'Шах можно закрыть — так и сделаем.' },
  ],

  /** checkEscape: capture the checking piece (never with the king — that case is not asked). */
  'v3.quiz.explain.escCapture': [
    { t: 'Здесь спасение простое — съесть того, кто напал на короля.' },
    { t: 'Кто шахует — того и съедим, и шаха как не бывало.' },
    { t: 'Съедим обидчика — и король спасён.' },
    { t: 'Нападающий сам стоит под ударом — съедим его!' },
    { t: 'Надёжнее съесть нахала — и король вздохнёт спокойно.' },
    { t: 'Короля выручим так: съедим того, кто дал шах.' },
  ],

  /** danger: which of our pieces (victim) is under attack («под ударом», never «без защиты» — it may be defended). */
  'v3.quiz.explain.danger': [
    { t: 'Под ударом {твой} {конь} — соперник может {его} съесть.' },
    { t: 'Соперник может забрать {коня} — вот в чём опасность.' },
    { t: 'Соперник уже дотягивается до {коня:gen}.' },
    { t: 'В опасности {твой} {конь} — сигнал тревоги!' },
    { t: 'Вот кого могут съесть — {твоего} {коня}.' },
    { t: 'Смотри: {твой} {конь} на прицеле у соперника.' },
    { t: 'Прямо в {коня} целится соперник.' },
  ],
};
